// TMOS Setup Wizard - PMO Setup Step
// Date: December 19, 2025
// Purpose: PMO/Transformation program setup step (optional)

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';

interface PMOSetupStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

export const PMOSetupStep: React.FC<PMOSetupStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [skipPMO, setSkipPMO] = useState(setupData.skipPMO || false);
  const [programName, setProgramName] = useState(setupData.programName || '');
  const [programDescription, setProgramDescription] = useState(setupData.programDescription || '');

  const handleContinue = () => {
    if (skipPMO) {
      onComplete({
        pmo_setup: {
          skipped: true
        },
        skipPMO: true
      });
    } else {
      onComplete({
        pmo_setup: {
          programName,
          programDescription
        },
        programName,
        programDescription
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg">
        <Checkbox
          checked={skipPMO}
          onChange={(checked) => setSkipPMO(checked)}
        />
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {t('setupWizard.pmo.skip') || 'Skip PMO Setup'}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {t('setupWizard.pmo.skipDescription') || 
              'You can set up your transformation program later'}
          </div>
        </div>
      </div>

      {!skipPMO && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('setupWizard.pmo.programName') || 'Program Name'}
            </label>
            <Input
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
              placeholder="Enter program name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('setupWizard.pmo.description') || 'Description'}
            </label>
            <Textarea
              value={programDescription}
              onChange={(e) => setProgramDescription(e.target.value)}
              placeholder="Enter program description"
              rows={4}
            />
          </div>

          <p className="text-sm text-gray-500">
            {t('setupWizard.pmo.note') || 
              'Note: You can add visions, objectives, strategies, and projects after setup is complete.'}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={loading}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </div>
  );
};

